import { NEW_POSTS } from "@/data/posts-new";

export const POSTS = [
  ...NEW_POSTS,
  {
    slug: "split-bills-with-friends",
    title: "How to Split Bills With Friends Without the Awkwardness",
    excerpt:
      "Dinner ends, the bill arrives, and everyone suddenly checks their phone. Here is how students can handle shared expenses without ruining the vibe, or the friendship.",
    category: "Shared Expenses",
    dateISO: "2026-07-12",
    dateLabel: "12 July 2026",
    readMinutes: 5,
    blocks: [
      {
        type: "p",
        text: "Every student group has the same ritual. The food was great, the conversation was better, and then the bill lands on the table. Suddenly everyone is very interested in their shoes. Splitting shared expenses is one of the most common money situations students in India face, and one of the least talked about.",
      },
      { type: "h2", text: "Why splitting gets awkward" },
      {
        type: "p",
        text: "It is rarely about the money itself. It is about not wanting to seem stingy, not remembering who paid last time, and the quiet resentment that builds when the same person always covers the auto ride home. When nobody tracks anything, everyone assumes they are the one paying more.",
      },
      {
        type: "ul",
        items: [
          "Friends have different budgets, but nobody says it out loud.",
          "Small amounts (₹60 here, ₹120 there) feel too petty to mention, until they add up.",
          "Asking to be paid back feels like accusing a friend of cheating you.",
        ],
      },
      { type: "h2", text: "Simple rules that keep friendships safe" },
      {
        type: "ul",
        items: [
          "Decide how you are splitting before you order, not after the bill arrives.",
          "One person pays, and the expense gets logged immediately. Memory is a terrible accountant.",
          "Settle up weekly instead of chasing every single rupee. Small balances roll into the next outing.",
          "Rotate who pays for the little things like chai, so generosity is shared too.",
        ],
      },
      { type: "h2", text: "Let an app remember so you don't have to" },
      {
        type: "p",
        text: "This is exactly why Splurge has Group Settle built in. Log a shared dinner once, and the app tracks who owes whom across the whole group. There is no mental maths at the table, no forgotten UPI requests, and no awkward reminders, because the app does the remembering for you. An expense tracker for students should make friendship easier, not turn it into accounting.",
      },
      {
        type: "p",
        text: "The goal is simple: money should never be the reason a good group stops going out. Agree on the system once, and then forget about it.",
      },
    ],
  },
  {
    slug: "small-spending-real-cost",
    title: "What ₹150 a Day Really Costs You: The Maths of Small Spending",
    excerpt:
      "A chai here, a snack there, a quick delivery order. Small daily spends feel harmless until you see what they equal in the things you actually want.",
    category: "Mindful Spending",
    dateISO: "2026-07-08",
    dateLabel: "8 July 2026",
    readMinutes: 4,
    blocks: [
      {
        type: "p",
        text: "Nobody feels poor because of one ₹150 expense. It is a reasonable amount: a coffee, a snack after class, a short cab ride when it rains. The problem is that small spends are invisible. They never feel like decisions, so they never get counted.",
      },
      { type: "h2", text: "The maths nobody does" },
      {
        type: "p",
        text: "₹150 a day is roughly ₹4,500 a month. Over a year, that is more than ₹54,000. That's enough for a decent phone, two return flights home, a professional course, or about twenty concert tickets. None of this means you should never buy chai. It means the trade-off deserves to be seen, not hidden.",
      },
      { type: "h2", text: "This is not about quitting chai" },
      {
        type: "p",
        text: "Most budgeting advice fails students because it is built on guilt: stop eating out, stop having fun, stop living. That approach lasts about a week. Mindful spending works differently. You keep the small joys, but you see them clearly enough to choose which ones are worth it.",
      },
      {
        type: "ul",
        items: [
          "Give small spends a weekly cap instead of banning them. A limit you chose feels very different from a rule imposed on you.",
          "Pick swap days: cook or eat at the mess twice a week, go out the rest without guilt.",
          "Compare against a wishlist. When ₹4,500 a month can become the shoes you actually want, the daily snack starts competing with a real goal.",
        ],
      },
      { type: "h2", text: "Make the trade-off visible" },
      {
        type: "p",
        text: "Splurge's Impact Calculator exists for exactly this moment. Log an expense and see what it could equal in real-life items: movie nights, concert tickets, or progress toward something on your wishlist. When every amount means something, mindful spending stops feeling like a restriction and starts feeling like a choice you are in control of.",
      },
    ],
  },
  {
    slug: "peer-pressure-spending",
    title: "Peer Pressure Spending: Why We Buy Things We Don't Want",
    excerpt:
      "Everyone ordered dessert, so you did too. Everyone has those shoes, so now you want them. An honest look at social spending, and how to stay yourself.",
    category: "Social Awareness",
    dateISO: "2026-07-03",
    dateLabel: "3 July 2026",
    readMinutes: 5,
    blocks: [
      {
        type: "p",
        text: "Think about the last thing you bought. Now think about why. For a surprising number of purchases, the honest answer is not “I wanted it” but “everyone around me had it.” That is peer pressure spending, and for students it is completely normal. Our social lives and our spending are deeply connected.",
      },
      { type: "h2", text: "The psychology behind it" },
      {
        type: "p",
        text: "Spending is social glue. Splitting the expensive café bill says “I belong here.” Owning what your friends own says “I am one of you.” Add Instagram, where everyone posts their best purchases and none of their bank balances, and it starts to feel like everyone can afford everything except you. They can't. You are comparing your behind-the-scenes with everyone else's highlight reel.",
      },
      { type: "h2", text: "Signs you are spending socially, not personally" },
      {
        type: "ul",
        items: [
          "You order more when you are with certain people than when you are alone.",
          "You have bought something mainly because declining felt embarrassing.",
          "You feel a small wave of regret within a day of a group purchase.",
          "You hide what you spent from yourself: no screenshots, no checking the balance after.",
        ],
      },
      { type: "h2", text: "Scripts that actually work" },
      {
        type: "p",
        text: "You rarely need a big speech. “I am saving for something” is a complete sentence, and most friends respect it instantly. So is suggesting the plan instead of rejecting it: the same friends, a cheaper place, the same fun. The person who suggests the affordable option is usually doing half the group a quiet favour.",
      },
      { type: "h2", text: "Awareness beats willpower" },
      {
        type: "p",
        text: "You cannot out-discipline a social pattern you have never seen. Splurge was built around this idea: track what you spend socially, see the pattern with student-first insights based on Indian trends, and decide for yourself what is worth it. Not less fun, just fewer purchases you never really wanted.",
      },
    ],
  },
  {
    slug: "student-monthly-budget-guide",
    title: "A Student Budget That Actually Survives the Month",
    excerpt:
      "Most student budgets die by the second weekend. Here is a realistic system built around pocket money, social life, and the occasional emergency auto ride.",
    category: "Budgeting",
    dateISO: "2026-06-28",
    dateLabel: "28 June 2026",
    readMinutes: 6,
    blocks: [
      {
        type: "p",
        text: "The reason most budgets fail students is simple: they are designed like diets. Too strict, no room for real life, abandoned at the first birthday dinner. A budget that works is not a cage. It is a rough map you actually look at.",
      },
      { type: "h2", text: "Step 1: Know your real number" },
      {
        type: "p",
        text: "Add up what you actually have for the month: pocket money, any freelance or internship income, and nothing else. Do not budget money you hope will appear. If your number is ₹8,000, that is the number. Every decision gets easier once it is honest.",
      },
      { type: "h2", text: "Step 2: Split it into three buckets" },
      {
        type: "ul",
        items: [
          "Essentials: food, travel, data, supplies. This is usually 50–60% of the total.",
          "Social: eating out, movies, events. Yes, this gets its own bucket, around 20–30%.",
          "Goals and buffer: the shoes, the trip, the emergency recharge. Even ₹500 a month here builds the habit.",
        ],
      },
      {
        type: "p",
        text: "The exact percentages matter less than the separation. When social money has its own bucket, spending it stops feeling like failure, and overspending it becomes a visible choice instead of a slow leak.",
      },
      { type: "h2", text: "Step 3: Do a ten-minute weekly check-in" },
      {
        type: "p",
        text: "Monthly budgets fail silently; weekly ones fail loudly, early, and fixably. Once a week, look at what you spent and what is left. That is it. Splurge's Spending Ledger gives you weekly and monthly summaries with no complicated setup, so the check-in takes minutes instead of a spreadsheet session.",
      },
      { type: "h2", text: "Step 4: Protect the fun line" },
      {
        type: "p",
        text: "A budget with zero fun is a budget with zero future. Decide what you refuse to give up (Sunday biryani, one concert a semester) and protect it deliberately. Cutting everything you love to save money is just as unsustainable as spending everything you have.",
      },
    ],
  },
  {
    slug: "digital-money-guide-students",
    title: "UPI, Wallets, Cash: A Student's Guide to Digital Money in India",
    excerpt:
      "Paying with a tap is effortless, which is exactly the problem. How Indian students can enjoy digital money without losing track of it, plus the safety basics everyone should know.",
    category: "Financial Literacy",
    dateISO: "2026-06-20",
    dateLabel: "20 June 2026",
    readMinutes: 5,
    blocks: [
      {
        type: "p",
        text: "India's students grew up in the UPI era. Splitting a bill, paying the canteen, sending a friend ₹50: everything is a QR scan away. Digital payments are genuinely great. But they removed something useful: the feeling of money leaving your hand.",
      },
      { type: "h2", text: "Frictionless money is easy to lose track of" },
      {
        type: "p",
        text: "Cash had a built-in budget: when the wallet was empty, you stopped. UPI never feels empty until the bank balance is. That is why the first financial literacy skill for digital money is not earning or investing. It is simply tracking. Every tap, logged. An expense tracker for students turns invisible taps back into visible decisions.",
      },
      { type: "h2", text: "Safety basics that are non-negotiable" },
      {
        type: "ul",
        items: [
          "Your UPI PIN is only ever for sending money. Anyone who asks for it to receive money is scamming you.",
          "Never share OTPs, and treat “urgent” payment requests, even from friends' hacked accounts, with suspicion. Call first.",
          "Check the receiver's name on the confirmation screen before every payment, not after.",
          "Keep your daily transaction limit modest. It is a seatbelt, not a cage.",
        ],
      },
      { type: "h2", text: "Use the convenience, keep the awareness" },
      {
        type: "p",
        text: "The answer is not going back to cash. It is pairing India's brilliant payment rails with one small habit: a ten-second log after each spend. Do that for a month and you will know your own money better than most adults do, and that is the whole point of starting financial literacy early.",
      },
    ],
  },
];
